import { Box, Card, CardContent, CardHeader, Grid, Skeleton, useTheme } from '@mui/material';
import type { ComponentProps } from 'react';

import { useBreakpoint } from '../..//hooks/useBreakpoint/useBreakpoint';
import { DataCardItem } from '../DataCardItem/DataCardItem';

const DataCardSkeleton = () => {
  return (
    <Grid container spacing={2} data-testid="data-card-skeleton-container">
      <DataCard.Item isLoading={true}>
        <Skeleton height={40} width="100%" />
      </DataCard.Item>
      <DataCard.Item isLoading={true}>
        <Skeleton height={40} width="100%" />
      </DataCard.Item>
      <DataCard.Item isLoading={true}>
        <Skeleton height={40} width="100%" />
      </DataCard.Item>
    </Grid>
  );
};

const DataCardHeader = (props: ComponentProps<typeof CardHeader> & { isLoading?: boolean }) => {
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const { isLoading, ...rest } = props;

  if (isLoading) {
    return (
      <Box
        display="flex"
        paddingX={2.5}
        gap={2.5}
        alignItems="center"
        height="64px"
        sx={{
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Skeleton variant="circular" width={30} height={30} />
        <Skeleton width="100%" height={40} />
      </Box>
    );
  }

  return (
    <CardHeader
      titleTypographyProps={{
        color: theme.palette.secondary.main,
        fontSize: isMobile ? '16px' : '24px',
        fontWeight: 'bold',
        variant: 'h5',
      }}
      title={props.title}
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
      {...rest}
    />
  );
};

type Props = {
  isLoading?: boolean;
  children: React.ReactNode;
  dataTestId?: string;
  slotProps?: {
    header: ComponentProps<typeof DataCardHeader>;
  };
};

export const DataCard = ({ isLoading, children, slotProps, dataTestId }: Props) => {
  return (
    <Card variant="outlined" sx={{ height: '100%' }} data-testid={dataTestId}>
      <DataCardHeader {...slotProps?.header} isLoading={isLoading} />
      <CardContent sx={{ padding: 3.75 }}>
        {isLoading ? <DataCardSkeleton /> : <Grid container>{children}</Grid>}
      </CardContent>
    </Card>
  );
};

DataCard.Header = DataCardHeader;
DataCard.Item = DataCardItem;
